// src/components/admin/AdminLayout.js — Vertical Left Sidebar Layout
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Squares2X2Icon, CubeIcon, QrCodeIcon, ClipboardDocumentListIcon,
  ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon,
  ChartBarIcon, ExclamationTriangleIcon, CalendarDaysIcon,
  ShoppingBagIcon, UsersIcon, ShieldCheckIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const ALL_NAV = [
  { label:'Dashboard',       icon:Squares2X2Icon,            route:'/admin/dashboard',        perm:'dashboard'        },
  { label:'Products',        icon:CubeIcon,                  route:'/admin/products',         perm:'products'         },
  { label:'Fridges',         icon:QrCodeIcon,                route:'/admin/fridges',          perm:'fridges'          },
  { label:'Assign Products', icon:ClipboardDocumentListIcon, route:'/admin/assign-products',  perm:'assign_products'  },
  { label:'Stock Monitor',   icon:ExclamationTriangleIcon,   route:'/admin/stock-monitoring', perm:'stock_monitor'    },
  { label:'Sales Report',    icon:ChartBarIcon,              route:'/admin/sales-report',     perm:'sales_report'     },
  { label:'Refill Schedule', icon:CalendarDaysIcon,          route:'/admin/refill-schedule',  perm:'refill_schedule'  },
  { label:'Orders',          icon:ShoppingBagIcon,           route:'/admin/orders',           perm:'orders'           },
  { label:'Customers',       icon:UsersIcon,                 route:'/admin/customers',        perm:'customers'        },
  { label:'Staff Manager',   icon:ShieldCheckIcon,           route:'/admin/staff',            perm:'customers', adminOnly:true },
];

const SB_WIDTH     = 240;
const SB_COLLAPSED = 64;

const AdminLayout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { admin, logout, hasPermission, isAdmin } = useAuth();

  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // desktop sidebar collapsed state

  const isActive = (r) => location.pathname === r;

  const visibleNav = ALL_NAV.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    return hasPermission(item.perm);
  });

  const drawerNav = ALL_NAV.filter(item => !(item.adminOnly && !isAdmin));

  const roleColor  = isAdmin ? '#059669' : '#0891b2';
  const roleBg     = isAdmin ? '#ecfdf5'  : '#eff6ff';
  const roleBorder = isAdmin ? '#a7f3d0'  : '#bfdbfe';
  const roleLabel  = isAdmin ? 'Admin'    : 'Staff';

  const sw = sidebarOpen ? SB_WIDTH : SB_COLLAPSED;

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; background:#f1f5f9; color:#1e293b; }

    /* ── SIDEBAR ─────────────────────────────────────────────────────── */
    .sb {
      display: none;
    }
    @media(min-width:900px) {
      .sb {
        display:         flex;
        flex-direction:  column;
        position:        fixed;
        top:             0;
        left:            0;
        width:           ${sw}px;
        height:          100vh;
        background:      #ffffff;
        border-right:    1px solid #e2e8f0;
        box-shadow:      2px 0 16px rgba(0,0,0,0.06);
        z-index:         200;
        transition:      width 0.25s cubic-bezier(0.4,0,0.2,1);
        overflow:        hidden;
      }
    }

    /* Logo strip */
    .sb-logo {
      display:          flex;
      align-items:      center;
      gap:              0.65rem;
      padding:          0 1.1rem;
      height:           64px;
      min-height:       64px;
      background:       linear-gradient(135deg,#059669 0%,#0891b2 100%);
      flex-shrink:      0;
      overflow:         hidden;
      cursor:           pointer;
    }
    .sb-logo-img-wrap {
      width:            38px;
      height:           38px;
      min-width:        38px;
      border-radius:    10px;
      background:       #ffffff;
      display:          flex;
      align-items:      center;
      justify-content:  center;
      flex-shrink:      0;
      overflow:         hidden;
    }
    .sb-logo img {
      height:      30px;
      width:       30px;
      object-fit:  contain;
      flex-shrink: 0;
    }
    .sb-logo-text { overflow:hidden; }
    .sb-logo-name {
      font-size:      0.95rem;
      font-weight:    900;
      color:          #ffffff;
      white-space:    nowrap;
      line-height:    1.2;
    }
    .sb-logo-sub {
      font-size:   0.6rem;
      color:       rgba(255,255,255,0.75);
      font-weight: 600;
      white-space: nowrap;
    }

    /* Nav section label */
    .sb-section {
      font-size:      0.58rem;
      font-weight:    800;
      color:          #9ca3af;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding:        0.9rem 1.2rem 0.35rem;
      white-space:    nowrap;
      overflow:       hidden;
    }

    /* Nav scroll area */
    .sb-nav {
      flex:            1;
      overflow-y:      auto;
      overflow-x:      hidden;
      padding:         0.25rem 0;
      scrollbar-width: thin;
      scrollbar-color: #e2e8f0 transparent;
    }

    /* Nav buttons */
    .sb-btn {
      display:         flex;
      align-items:     center;
      gap:             0.78rem;
      width:           100%;
      padding:         0.78rem 1.2rem;
      background:      none;
      border:          none;
      border-left:     3px solid transparent;
      cursor:          pointer;
      font-family:     'Nunito',sans-serif;
      font-size:       0.875rem;
      font-weight:     600;
      color:           #374151;
      text-align:      left;
      transition:      all 0.15s ease;
      white-space:     nowrap;
      overflow:        hidden;
    }
    .sb-btn svg {
      width:       1.05rem;
      height:      1.05rem;
      flex-shrink: 0;
      min-width:   1.05rem;
    }
    .sb-btn:hover {
      background:        #f0fdf4;
      color:             #059669;
      border-left-color: #059669;
    }
    .sb-btn.on {
      background:  linear-gradient(135deg,#ecfdf5,#e8f5fe);
      color:       #059669;
      border-left: 3px solid #059669;
      font-weight: 700;
    }
    .sb-btn-label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

    /* Sidebar footer (user info) */
    .sb-footer {
      border-top:  1px solid #f1f5f9;
      padding:     0.85rem 1.1rem;
      flex-shrink: 0;
      overflow:    hidden;
    }
    .sb-user {
      font-size:    0.72rem;
      color:        #6b7280;
      font-weight:  600;
      white-space:  nowrap;
      overflow:     hidden;
      text-overflow:ellipsis;
      margin-bottom:0.55rem;
    }
    .sb-logout {
      display:         flex;
      align-items:     center;
      justify-content: center;
      gap:             0.4rem;
      width:           100%;
      padding:         0.55rem 0.9rem;
      border-radius:   0.6rem;
      background:      #fff1f2;
      color:           #e11d48;
      border:          1.5px solid #fecdd3;
      font-weight:     700;
      font-size:       0.8rem;
      cursor:          pointer;
      transition:      all 0.2s;
      font-family:     'Nunito',sans-serif;
      white-space:     nowrap;
    }
    .sb-logout:hover { background:#e11d48; color:white; }
    .sb-logout svg   { width:0.9rem; height:0.9rem; flex-shrink:0; }

    /* Toggle collapse button */
    .sb-toggle {
      position:        absolute;
      top:             50%;
      right:           -12px;
      transform:       translateY(-50%);
      width:           24px;
      height:          24px;
      border-radius:   50%;
      background:      #059669;
      color:           white;
      border:          2px solid white;
      cursor:          pointer;
      display:         flex;
      align-items:     center;
      justify-content: center;
      box-shadow:      0 2px 8px rgba(5,150,105,0.45);
      z-index:         10;
      transition:      background 0.15s;
      flex-shrink:     0;
    }
    .sb-toggle:hover { background:#047857; }
    .sb-toggle svg   { width:0.7rem; height:0.7rem; }

    /* ── HEADER (desktop: no nav, just user info) ─────────────────── */
    .ah {
      position:        fixed;
      top:             0;
      left:            ${sw}px;
      right:           0;
      height:          64px;
      z-index:         100;
      background:      rgba(255,255,255,0.97);
      backdrop-filter: blur(14px);
      border-bottom:   1px solid rgba(5,150,105,0.15);
      box-shadow:      0 2px 16px rgba(0,0,0,0.06);
      transition:      left 0.25s cubic-bezier(0.4,0,0.2,1);
      display:         none;
    }
    @media(min-width:900px) {
      .ah { display:flex; align-items:center; }
    }
    .ah-in {
      width:           100%;
      padding:         0 1.5rem;
      display:         flex;
      align-items:     center;
      justify-content: flex-end;
      gap:             0.5rem;
    }
    @media(min-width:1200px) { .ah-in { padding:0 2rem; } }

    /* Role chip */
    .ah-role-chip {
      display:      flex;
      align-items:  center;
      gap:          0.38rem;
      border-radius:9999px;
      padding:      0.28rem 0.65rem 0.28rem 0.28rem;
      border:       1px solid;
    }
    .ah-av {
      width:           26px;
      height:          26px;
      border-radius:   50%;
      display:         flex;
      align-items:     center;
      justify-content: center;
      color:           white;
      font-weight:     800;
      font-size:       0.65rem;
    }
    .ah-uname      { font-size:0.75rem; font-weight:700; color:#374151; }
    .ah-role-label { font-size:0.62rem; font-weight:800; padding:0.1rem 0.42rem;
                     border-radius:9999px; }

    .ah-logout {
      display:      flex;
      align-items:  center;
      gap:          0.32rem;
      padding:      0.42rem 1rem;
      border-radius:9999px;
      background:   #fff1f2;
      color:        #e11d48;
      border:       1.5px solid #fecdd3;
      font-weight:  700;
      font-size:    0.8rem;
      cursor:       pointer;
      transition:   all 0.2s;
      font-family:  'Nunito',sans-serif;
      white-space:  nowrap;
    }
    .ah-logout:hover { background:#e11d48; color:white; border-color:#e11d48; }
    .ah-logout svg   { width:0.85rem; height:0.85rem; }

    /* ── MOBILE HEADER ────────────────────────────────────────────── */
    .mh {
      position:        fixed;
      top:             0;
      left:            0;
      right:           0;
      height:          56px;
      z-index:         100;
      background:      rgba(255,255,255,0.97);
      backdrop-filter: blur(14px);
      border-bottom:   1px solid rgba(5,150,105,0.15);
      box-shadow:      0 2px 16px rgba(0,0,0,0.06);
      display:         flex;
      align-items:     center;
      padding:         0 1rem;
      justify-content: space-between;
      gap:             0.5rem;
    }
    @media(min-width:900px) { .mh { display:none; } }
    .mh-logo {
      display:     flex;
      align-items: center;
      gap:         0.45rem;
      cursor:      pointer;
    }
    .mh-logo img {
      height:      28px;
      object-fit:  contain;
    }
    .mh-logo-name {
      font-size:   0.9rem;
      font-weight: 900;
      background:  linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip:text;
      -webkit-text-fill-color:transparent;
    }
    .mh-acts { display:flex; align-items:center; gap:0.4rem; }
    .mh-ham {
      width:           34px;
      height:          34px;
      border-radius:   0.45rem;
      display:         flex;
      align-items:     center;
      justify-content: center;
      background:      #f8fafc;
      border:          1px solid #e2e8f0;
      color:           #475569;
      cursor:          pointer;
      transition:      all 0.2s;
    }
    .mh-ham:hover { background:#f0fdf4; color:#059669; border-color:#a7f3d0; }
    .mh-ham svg   { width:1rem; height:1rem; }
    .mh-logout {
      display:      flex;
      align-items:  center;
      gap:          0.28rem;
      padding:      0.32rem 0.7rem;
      border-radius:9999px;
      background:   #fff1f2;
      color:        #e11d48;
      border:       1.5px solid #fecdd3;
      font-weight:  700;
      font-size:    0.72rem;
      cursor:       pointer;
      transition:   all 0.2s;
      font-family:  'Nunito',sans-serif;
    }
    .mh-logout:hover { background:#e11d48; color:white; }
    .mh-logout svg   { width:0.8rem; height:0.8rem; }

    /* ── MOBILE DRAWER ────────────────────────────────────────────── */
    .adw { position:fixed; inset:0; z-index:300; pointer-events:none; }
    .adw.open { pointer-events:all; }
    .adw-ov { position:absolute; inset:0; background:rgba(0,0,0,0); transition:background 0.3s; }
    .adw.open .adw-ov { background:rgba(0,0,0,0.42); }
    .adw-panel {
      position:    absolute;
      top:         0; left:0;
      width:       min(285px,88vw);
      height:      100%;
      background:  white;
      transform:   translateX(-100%);
      transition:  transform 0.32s cubic-bezier(0.4,0,0.2,1);
      display:     flex;
      flex-direction:column;
      box-shadow:  8px 0 30px rgba(0,0,0,0.12);
    }
    .adw.open .adw-panel { transform:translateX(0); }
    .adw-hdr {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      padding:         0.85rem 1.1rem;
      border-bottom:   1px solid #f3f4f6;
      gap:             0.5rem;
    }
    .adw-logo        { display:flex; align-items:center; gap:0.45rem; }
    .adw-logo img    { height:26px; width:auto; object-fit:contain; }
    .adw-logo-name   { font-size:0.9rem; font-weight:900;
                       background:linear-gradient(135deg,#059669,#0891b2);
                       -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
    .adw-badge       { color:white; font-size:0.6rem; font-weight:700;
                       padding:0.16rem 0.52rem; border-radius:9999px; flex-shrink:0; }
    .adw-close       { width:26px; height:26px; border-radius:50%; background:#f8fafc;
                       border:1px solid #e2e8f0; color:#475569; cursor:pointer;
                       display:flex; align-items:center; justify-content:center; }
    .adw-close svg   { width:0.85rem; height:0.85rem; }
    .adw-nav         { flex:1; padding:0.4rem 0; overflow-y:auto; }
    .adw-section     { font-size:0.62rem; font-weight:800; color:#9ca3af;
                       text-transform:uppercase; letter-spacing:0.6px;
                       padding:0.7rem 1.25rem 0.25rem; }
    .adw-link        { display:flex; align-items:center; gap:0.62rem; padding:0.72rem 1.25rem;
                       font-size:0.88rem; font-weight:700; color:#374151; border:none; background:none;
                       cursor:pointer; border-left:3px solid transparent; transition:all 0.2s;
                       width:100%; font-family:'Nunito',sans-serif; text-align:left; }
    .adw-link svg    { width:1.05rem; height:1.05rem; flex-shrink:0; }
    .adw-link:hover  { background:#f0fdf4; color:#059669; border-left-color:#059669; }
    .adw-link.on     { background:#ecfdf5; color:#059669; border-left-color:#059669; font-weight:800; }
    .adw-link-locked { display:flex; align-items:center; gap:0.62rem; padding:0.72rem 1.25rem;
                       font-size:0.88rem; font-weight:700; color:#cbd5e1;
                       border-left:3px solid transparent; width:100%; cursor:not-allowed; }
    .adw-link-locked svg { width:1.05rem; height:1.05rem; flex-shrink:0; }
    .adw-ftr         { padding:0.85rem 1.25rem; border-top:1px solid #f3f4f6; }
    .adw-user        { font-size:0.74rem; color:#6b7280; margin-bottom:0.6rem; font-weight:600; }
    .adw-logout      { width:100%; display:flex; align-items:center; justify-content:center;
                       gap:0.42rem; padding:0.65rem 1rem; border-radius:0.7rem;
                       background:#fff1f2; color:#e11d48; border:1.5px solid #fecdd3;
                       font-weight:700; font-size:0.85rem; cursor:pointer; transition:all 0.2s;
                       font-family:'Nunito',sans-serif; }
    .adw-logout:hover { background:#e11d48; color:white; }
    .adw-logout svg   { width:0.95rem; height:0.95rem; }

    /* ── STAFF BANNER ─────────────────────────────────────────────── */
    .staff-banner {
      background:    linear-gradient(135deg,#eff6ff,#f0f9ff);
      border:        1px solid #bfdbfe;
      border-radius: 0.875rem;
      padding:       0.65rem 1.1rem;
      margin-bottom: 1.25rem;
      display:       flex;
      align-items:   center;
      gap:           0.65rem;
      font-size:     0.82rem;
      font-weight:   700;
      color:         #1d4ed8;
    }
    .staff-banner svg { width:1rem; height:1rem; flex-shrink:0; }

    /* ── PAGE CONTENT ─────────────────────────────────────────────── */
    /* Mobile */
    .adm-page {
      min-height:  100vh;
      padding:     calc(56px + 1rem) 0.75rem 1rem;
    }
    @media(min-width:480px) { .adm-page { padding:calc(56px + 1.25rem) 1rem 1.25rem; } }

    /* Desktop: offset for fixed sidebar + fixed header */
    @media(min-width:900px) {
      .adm-page {
        margin-left: ${sw}px;
        padding:     calc(64px + 1.75rem) 1.5rem 1.75rem;
        transition:  margin-left 0.25s cubic-bezier(0.4,0,0.2,1);
        min-height:  100vh;
      }
    }
    @media(min-width:1024px) {
      .adm-page { padding:calc(64px + 2rem) 2rem 2rem; }
    }
    .adm-content { max-width:1400px; margin:0 auto; }
  `;

  return (
    <>
      <style>{css}</style>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="sb">
        {/* Logo */}
        <div className="sb-logo" onClick={() => navigate('/admin/dashboard')}>
          <div className="sb-logo-img-wrap">
            <img src="/images/logo.png" alt="Fresh Hills"
              onError={e => { e.target.onerror=null; e.target.src='/favicon.ico'; }} />
          </div>
          {sidebarOpen && (
            <div className="sb-logo-text">
              <div className="sb-logo-name">Fresh Hills</div>
              <div className="sb-logo-sub">Admin Panel</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="sb-nav">
          {sidebarOpen && <div className="sb-section">Navigation</div>}
          {visibleNav.map(item => (
            <button key={item.route}
              className={`sb-btn${isActive(item.route) ? ' on' : ''}`}
              onClick={() => navigate(item.route)}
              title={!sidebarOpen ? item.label : ''}>
              <item.icon />
              {sidebarOpen && <span className="sb-btn-label">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Footer */}
        {sidebarOpen && (
          <div className="sb-footer">
            <div className="sb-user">
              Logged in as <strong>{admin?.username}</strong>
              {' · '}
              <span style={{ color: roleColor }}>{roleLabel}</span>
            </div>
            <button className="sb-logout" onClick={logout}>
              <ArrowRightOnRectangleIcon /> Logout
            </button>
          </div>
        )}

        {/* Toggle button */}
        <button className="sb-toggle" onClick={() => setSidebarOpen(v => !v)}
          title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </button>
      </aside>

      {/* ── DESKTOP HEADER (user info only) ── */}
      <header className="ah">
        <div className="ah-in">
          <div className="ah-role-chip" style={{ background: roleBg, borderColor: roleBorder }}>
            <div className="ah-av"
              style={{ background: `linear-gradient(135deg,${roleColor},#0891b2)` }}>
              {admin?.username?.[0]?.toUpperCase() || 'U'}
            </div>
            <span className="ah-uname">{admin?.username || 'User'}</span>
            <span className="ah-role-label" style={{ background: roleColor, color:'white' }}>
              {roleLabel}
            </span>
          </div>
          <button className="ah-logout" onClick={logout}>
            <ArrowRightOnRectangleIcon /><span>Logout</span>
          </button>
        </div>
      </header>

      {/* ── MOBILE HEADER ── */}
      <header className="mh">
        <div className="mh-logo" onClick={() => navigate('/admin/dashboard')}>
          <img src="/images/logo.png" alt="Fresh Hills"
            onError={e => { e.target.onerror=null; e.target.style.display='none'; }} />
          <span className="mh-logo-name">Fresh Hills</span>
        </div>
        <div className="mh-acts">
          <button className="mh-logout" onClick={logout}>
            <ArrowRightOnRectangleIcon /><span>Logout</span>
          </button>
          <button className="mh-ham" onClick={() => setDrawerOpen(true)}>
            <Bars3Icon />
          </button>
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      <div className={`adw${drawerOpen ? ' open' : ''}`}>
        <div className="adw-ov" onClick={() => setDrawerOpen(false)} />
        <div className="adw-panel">
          <div className="adw-hdr">
            <div className="adw-logo">
              <img src="/images/logo.png" alt="Fresh Hills"
                onError={e => { e.target.onerror=null; e.target.style.display='none'; }} />
              <span className="adw-logo-name">Fresh Hills</span>
            </div>
            <span className="adw-badge" style={{ background: roleColor }}>{roleLabel}</span>
            <button className="adw-close" onClick={() => setDrawerOpen(false)}><XMarkIcon /></button>
          </div>

          <nav className="adw-nav">
            <div className="adw-section">Navigation</div>
            {drawerNav.map(item => {
              const allowed = hasPermission(item.perm);
              if (!allowed) return (
                <div key={item.route} className="adw-link-locked">
                  <item.icon />{item.label}
                  <span style={{ marginLeft:'auto', fontSize:'0.62rem', color:'#94a3b8', fontWeight:700 }}>
                    🔒 Admin only
                  </span>
                </div>
              );
              return (
                <button key={item.route}
                  className={`adw-link${isActive(item.route) ? ' on' : ''}`}
                  onClick={() => { navigate(item.route); setDrawerOpen(false); }}>
                  <item.icon />{item.label}
                </button>
              );
            })}
          </nav>

          <div className="adw-ftr">
            <div className="adw-user">
              Logged in as <strong>{admin?.username}</strong>
              {' · '}
              <span style={{ color: roleColor, fontWeight:800 }}>{roleLabel}</span>
            </div>
            <button className="adw-logout" onClick={logout}>
              <ArrowRightOnRectangleIcon /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── PAGE CONTENT ── */}
      <main className="adm-page">
        <div className="adm-content">
          {!isAdmin && (
            <div className="staff-banner">
              <ShieldCheckIcon />
              You are logged in as <strong style={{ margin:'0 0.2rem' }}>{admin?.username}</strong> (Staff).
              Some features are restricted to Admin only.
            </div>
          )}
          {children}
        </div>
      </main>
    </>
  );
};

export default AdminLayout;
